/**
 * Custom Hook: useQualityUpdate
 * Manages state and logic for batch quality grade updates
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-003
 */

import { useState } from 'react';
import { inventoryBatchService } from '@/services/commerce/inventoryBatchService';
import type { BatchResponse, UpdateQualityRequest, QualityGrade } from '@/types/inventory';

interface UseQualityUpdateOptions {
  onSuccess?: (batch: BatchResponse) => void;
  onError?: (error: Error) => void;
}

interface UseQualityUpdateReturn {
  isUpdating: boolean;
  error: string | null;
  updateQuality: (batchId: number, data: UpdateQualityRequest) => Promise<BatchResponse | null>;
  reset: () => void;
  isDowngrade: (currentGrade: QualityGrade, newGrade: QualityGrade) => boolean;
}

/**
 * Grade priority for downgrade detection
 * Higher number = better grade
 */
const GRADE_PRIORITY = {
  A: 3,
  B: 2,
  C: 1
};

/**
 * Hook for managing batch quality updates
 */
export function useQualityUpdate(options: UseQualityUpdateOptions = {}): UseQualityUpdateReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Determine if grade change is a downgrade
   */
  function isDowngrade(currentGrade: QualityGrade, newGrade: QualityGrade): boolean {
    return GRADE_PRIORITY[newGrade] < GRADE_PRIORITY[currentGrade];
  }

  /**
   * Update batch quality grade
   */
  async function updateQuality(
    batchId: number,
    data: UpdateQualityRequest
  ): Promise<BatchResponse | null> {
    try {
      setIsUpdating(true);
      setError(null);

      const updatedBatch = await inventoryBatchService.updateBatchQuality(batchId, data);

      if (options.onSuccess) {
        options.onSuccess(updatedBatch);
      }

      return updatedBatch;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update batch quality';
      setError(errorMessage);

      if (options.onError) {
        options.onError(err instanceof Error ? err : new Error(errorMessage));
      }

      return null;
    } finally {
      setIsUpdating(false);
    }
  }

  /**
   * Reset hook state
   */
  function reset(): void {
    setIsUpdating(false);
    setError(null);
  }

  return {
    isUpdating,
    error,
    updateQuality,
    reset,
    isDowngrade
  };
}
